import { Text, TextButton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { MuiMarkdown } from "@src/appV2/lib/Markdown/MuiMarkdown";
import {
  InterviewStatus,
  type InterviewType,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { Link, useHistory } from "react-router-dom";

import { type PlacementDetail } from "../api/useGetPlacement";
import { useAddInterviewToCalendar } from "../hooks/useAddInterviewToCalendar";
import { useInterviewMessage } from "../hooks/useInterviewMessage";
import { getPlacementChatPath } from "../paths";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";
import { AddToCalendarCard } from "./AddToCalendarCard";

interface PlacementApplicationMessageProps {
  interviews: PlacementDetail["interviews"];
  workerTimezone: string;
  hasPendingInterviewRequests: boolean;
  applicationStatus?: PlacementApplicationStatus;
  workplaceName: string;
  placementId: string;
  workplaceId: string;
  interviewInstructions?: string;
  hasActiveBan: boolean;
  interviewType?: InterviewType;
}

export function PlacementApplicationMessage(props: PlacementApplicationMessageProps) {
  const {
    interviews,
    workerTimezone,
    hasPendingInterviewRequests,
    applicationStatus,
    workplaceName,
    placementId,
    workplaceId,
    interviewInstructions,
    hasActiveBan,
    interviewType,
  } = props;

  const history = useHistory();
  const { addToCalendar, isAddingToCalendar } = useAddInterviewToCalendar();

  const { hasBookedInterview, title, subtitle } = useInterviewMessage({
    interviews,
    workerTimezone,
    hasActiveBan,
  });

  if (hasPendingInterviewRequests && applicationStatus === PlacementApplicationStatus.APPLIED) {
    return (
      <Text variant="body2">
        {workplaceName} will get back to you in 3 business days, through your Clipboard Chat. If you
        don&apos;t hear back, you can reach out to them for an update{" "}
        <TextButton
          LinkComponent={Link}
          onClick={() => {
            history.push(
              getPlacementChatPath(
                placementId,
                workplaceId,
                "chat-with-placement-candidate-via-job-request"
              )
            );
          }}
        >
          here
        </TextButton>
        .
      </Text>
    );
  }

  return (
    <Stack spacing={4}>
      <Text semibold variant="body1">
        {title}
      </Text>

      {subtitle && <Text variant="body2">{subtitle}</Text>}

      {hasBookedInterview &&
        isDefined(interviewInstructions) &&
        interviewInstructions.trim().length > 0 && (
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Text semibold variant="body1">
                Important interview details:
              </Text>
            </Stack>
            <Box sx={{ pt: 1 }}>
              <MuiMarkdown variant="body2" lineHeight="1" listMarginTop={0}>
                {interviewInstructions}
              </MuiMarkdown>
            </Box>
          </Stack>
        )}

      {hasBookedInterview &&
        (() => {
          const bookedInterview = interviews
            .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
            .toSorted(
              (a, b) =>
                new Date(b.attributes.createdAt).getTime() -
                new Date(a.attributes.createdAt).getTime()
            )
            .at(0);
          const icsFileUrl = bookedInterview?.meta?.icsFileUrl;
          if (!isDefined(bookedInterview) || !isDefined(icsFileUrl)) {
            return undefined;
          }

          return (
            <AddToCalendarCard
              disabled={isAddingToCalendar}
              onClick={async () => {
                await addToCalendar({
                  placementId,
                  interviewId: bookedInterview.id,
                  interviewType: interviewType ?? bookedInterview.attributes.type,
                  icsFileUrl,
                });
              }}
            />
          );
        })()}
    </Stack>
  );
}
